import React, { useState } from 'react';
import {
  Button,
  Card,
  Modal,
  Space,
  Table,
  TablePaginationConfig,
  Input,
  Popover,
  Select,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, FormOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Rule, RulesWithDeviceType, DeviceType, SuperGroup, DeviceTypeSettings } from '@services/types';

import api from '@services/api';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import styles from '@layouts/firmwares/Rules/rules.module.scss';
import { selectSearchFilter } from '@services/utils';
import { AppContext } from '@layouts/AppRouter';
import UnusedRule from '@layouts/firmwares/UnusedRules';
import { Navigation } from '@constants/Navigation';

const { Column } = Table;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const getRuleStatus = (
  rule: RulesWithDeviceType,
): { status: 'active' | 'inactive-unused' | 'inactive-expired'; message: string; color: string } => {
  const { updateWindowStartDate, updateWindowEndDate, updateWindowStartTime, updateWindowEndTime, stbGroups } = rule;

  if (!stbGroups || stbGroups.length === 0) {
    return { status: 'inactive-unused', message: 'Inactive, unused', color: 'default' };
  }

  const now = moment();
  const currentDate = now.format('YYYY-MM-DD');
  const currentTime = now.format('HH:mm:ss');

  if (updateWindowEndDate && moment(currentDate).isAfter(updateWindowEndDate, 'day')) {
    return { status: 'inactive-expired', message: 'Inactive, expired', color: 'error' };
  }

  const hasTimeWindow = updateWindowStartDate || updateWindowEndDate || updateWindowStartTime || updateWindowEndTime;
  if (hasTimeWindow) {
    let isWithinWindow = true;

    if (updateWindowStartDate && moment(currentDate).isBefore(updateWindowStartDate, 'day')) {
      isWithinWindow = false;
    }

    if (isWithinWindow && updateWindowStartTime && updateWindowEndTime) {
      const startTime = moment(updateWindowStartTime, 'HH:mm:ss');
      const endTime = moment(updateWindowEndTime, 'HH:mm:ss');
      const current = moment(currentTime, 'HH:mm:ss');

      if (startTime.isAfter(endTime)) {
        if (current.isBefore(startTime) && current.isAfter(endTime)) {
          isWithinWindow = false;
        }
      } else {
        if (current.isBefore(startTime) || current.isAfter(endTime)) {
          isWithinWindow = false;
        }
      }
    }

    if (!isWithinWindow) {
      return { status: 'inactive-expired', message: 'Inactive, expired', color: 'error' };
    }
  }

  return { status: 'active', message: 'Active', color: 'success' };
};

const getRuleConfiguration = (
  rule: RulesWithDeviceType,
): { hasExtended: boolean; hasCompare: boolean; hasTimeWindow: boolean; hasChaining: boolean } => {
  const {
    updateWindowStartDate,
    updateWindowEndDate,
    updateWindowStartTime,
    updateWindowEndTime,
    enableLoaderCondition,
    enableFwCondition,
    enableUiCondition,
    disableExtendedVersioningSettings,
    enableChainingRule,
  } = rule;

  const hasTimeWindow = !!(
    updateWindowStartDate ||
    updateWindowEndDate ||
    updateWindowStartTime ||
    updateWindowEndTime
  );

  const hasExtendedVersioning =
    disableExtendedVersioningSettings &&
    (!disableExtendedVersioningSettings.disableExtendedLoaderVersioning ||
      !disableExtendedVersioningSettings.disableExtendedFwVersioning ||
      !disableExtendedVersioningSettings.disableExtendedUiVersioning);

  const hasCompareCondition = enableLoaderCondition || enableFwCondition || enableUiCondition;

  return {
    hasExtended: !!hasExtendedVersioning,
    hasCompare: !!hasCompareCondition,
    hasTimeWindow,
    hasChaining: !!enableChainingRule,
  };
};

const getStatusPopoverContent = (
  rule: RulesWithDeviceType,
  statusInfo: ReturnType<typeof getRuleStatus>,
): React.ReactNode => {
  const { status, message } = statusInfo;
  const { stbGroups } = rule;

  return (
    <div style={{ maxWidth: '300px' }}>
      <div
        style={{
          fontWeight: 600,
          marginBottom: '8px',
          fontSize: '13px',
          color: statusInfo.color === 'success' ? '#52c41a' : statusInfo.color === 'error' ? '#ff4d4f' : '#8c8c8c',
        }}
      >
        Rule Status: {message}
      </div>

      {status === 'inactive-unused' && (
        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
          {!stbGroups || stbGroups.length === 0
            ? 'Not attached to any group or attached groups are empty'
            : 'Rule is not currently in use'}
        </div>
      )}

      {status === 'inactive-expired' && (
        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>The time window for this rule has expired</div>
      )}

      {status === 'active' && (
        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Rule is currently active and in use</div>
      )}
    </div>
  );
};

const getConfigurationPopoverContent = (
  config: ReturnType<typeof getRuleConfiguration>,
  rule: RulesWithDeviceType,
): React.ReactNode => {
  const {
    updateWindowStartDate,
    updateWindowEndDate,
    updateWindowStartTime,
    updateWindowEndTime,
    updateWindowTimezone,
    enableLoaderCondition,
    enableFwCondition,
    enableUiCondition,
    disableExtendedVersioningSettings,
  } = rule;

  const formatDate = (date: any) => {
    if (!date) return null;
    if (date instanceof Date) return moment(date).format('YYYY-MM-DD');
    return String(date);
  };

  const formatTime = (time: any) => {
    if (!time) return null;
    if (time instanceof Date) return moment(time).format('HH:mm:ss');
    return String(time);
  };

  const startDate = formatDate(updateWindowStartDate);
  const endDate = formatDate(updateWindowEndDate);
  const startTime = formatTime(updateWindowStartTime);
  const endTime = formatTime(updateWindowEndTime);

  return (
    <div style={{ maxWidth: '350px' }}>
      {config.hasExtended && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Extended Versioning</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {!disableExtendedVersioningSettings?.disableExtendedLoaderVersioning && (
              <div>• Loader extended versioning enabled</div>
            )}
            {!disableExtendedVersioningSettings?.disableExtendedFwVersioning && (
              <div>• Firmware extended versioning enabled</div>
            )}
            {!disableExtendedVersioningSettings?.disableExtendedUiVersioning && (
              <div>• UI extended versioning enabled</div>
            )}
          </div>
        </div>
      )}

      {config.hasCompare && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Compare Rules</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {enableLoaderCondition && <div>• Loader version comparison</div>}
            {enableFwCondition && <div>• Firmware version comparison</div>}
            {enableUiCondition && <div>• UI version comparison</div>}
          </div>
        </div>
      )}

      {config.hasTimeWindow && (
        <div>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Time Window</div>
          {(startDate || endDate) && (
            <div style={{ marginBottom: '6px' }}>
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '2px' }}>DATE RANGE</div>
              <div style={{ fontSize: '12px' }}>
                {startDate || 'No start'} → {endDate || 'No end'}
              </div>
            </div>
          )}
          {startTime && endTime && (
            <div style={{ marginBottom: '6px' }}>
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '2px' }}>TIME RANGE</div>
              <div style={{ fontSize: '12px' }}>
                {startTime} - {endTime}
              </div>
            </div>
          )}
          {updateWindowTimezone && (
            <div>
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '2px' }}>TIMEZONE</div>
              <div style={{ fontSize: '12px' }}>{String(updateWindowTimezone)}</div>
            </div>
          )}
        </div>
      )}

      {config.hasChaining && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Chaining Rule</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Rule has chaining configuration enabled</div>
        </div>
      )}

      {rule.forceUpdate && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Forced Rule</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Allows firmware downgrade</div>
        </div>
      )}

      {rule.sendAllFirmwares && rule.deviceTypeSendAllFirmwares && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Send All at Once</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            Sends all firmware types (LOADER, FW, UI) in one response, bypassing normal sequential order and quotas
          </div>
        </div>
      )}

      {!config.hasExtended &&
        !config.hasCompare &&
        !config.hasTimeWindow &&
        !config.hasChaining &&
        !rule.forceUpdate &&
        !(rule.sendAllFirmwares && rule.deviceTypeSendAllFirmwares) && (
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Versioning: Common rule without extended versioning</div>
        )}
    </div>
  );
};

const Rules: React.FC = () => {
  const { user, columnsRules, setColumnsRules, columnsRulesEtc, setColumnsRulesEtc, deviceFilter, setDeviceFilter } =
    React.useContext(AppContext);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [isShowCustomFirmware1Version, setIsShowCustomFirmware1Version] = useState(true);
  const [isShowCustomFirmware2Version, setIsShowCustomFirmware2Version] = useState(true);
  const [isShowCustomFirmware3Version, setIsShowCustomFirmware3Version] = useState(true);
  const [rawRules, setRawRules] = useState<Rule[]>([]);
  const [rules, setRules] = useState<RulesWithDeviceType[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [deviceSetting, setDeviceSetting] = useState<DeviceTypeSettings>();
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType['id']>();
  const [selectedSuper, setSelectedSuper] = useState<SuperGroup['id']>();
  const [superGroups, setSuperGroups] = useState<SuperGroup[]>([]);
  const [searchResult, setSearchResult] = useState('');
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: rules.length > 10,
    total: rules.length,
  });

  React.useEffect(() => {
    setRules(
      rawRules.map((rule) => ({
        ...rule,
        deviceTypeName: deviceTypes.find((deviceType) => deviceType.id === rule.deviceTypeId)?.deviceName,
        superGroupName: superGroups.find((superGroup) => superGroup.id === rule.deviceTypeId)?.name,
      })),
    );
    setIsShowCustomFirmware1Version(
      rawRules.some((rule) => rule.customFirmware1VersionId && deviceSetting?.fwExtraName1?.length),
    );
    setIsShowCustomFirmware2Version(
      rawRules.some((rule) => rule.customFirmware2VersionId && deviceSetting?.fwExtraName2?.length),
    );
    setIsShowCustomFirmware3Version(
      rawRules.some((rule) => rule.customFirmware3VersionId && deviceSetting?.fwExtraName3?.length),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceTypes, rawRules, superGroups, deviceSetting]);

  const handleSearch = (newPagination = pagination) => {
    if (deviceFilter?.length) {
      api.rules
        .getRules({
          filterDeviceTypeIds: deviceFilter ? [deviceFilter] : [''],
          limit: newPagination.pageSize,
          offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
        })
        .then((data) => {
          setRawRules(data.elements);
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: data.totalElementCount || data.elements.length,
          });
          setDeviceFilter('');
        });
    } else {
      if (searchResult.length >= 0) {
        api.rules
          .getRules({
            filterDeviceTypeIds: selectedDeviceType ? [selectedDeviceType] : [''],
            filterSuperGroupId: selectedSuper,
            filter: searchResult,
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
          })
          .then((data) => {
            setRawRules(data.elements);
            setPagination({
              current: newPagination.current,
              pageSize: newPagination.pageSize,
              showSizeChanger: true,
              total: data.totalElementCount || data.elements.length,
            });
          });
      }
    }
  };

  React.useEffect(() => {
    handleSearch();
    if (selectedDeviceType) {
      api.administration.getDeviceTypeSettings(selectedDeviceType).then((item) => {
        setDeviceSetting(item);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceType, selectedSuper, searchResult.length]);

  React.useEffect(() => {
    if (user?.id) {
      Promise.all([
        api.administration.getDeviceTypes(),
        api.administration.getSuperGroups(),
        api.administration.getColumnsRules(user.id),
      ])
        .then(([deviceType, superGroup, rulesColumn]) => {
          if (deviceType && superGroup) {
            setDeviceTypes(deviceType);
            setSuperGroups(superGroup);
            setColumnsRules(rulesColumn.columns);
            setColumnsRulesEtc(rulesColumn.etc);

            setLoading(false);
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error('Firmwares was not loaded due to error');
        });
    }
  }, []);

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig) => {
      setPagination(newPagination);
      handleSearch(newPagination);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDeviceType, selectedSuper],
  );

  const showDeleteConfirm = (rule: Rule) => {
    Modal.confirm({
      title: 'Are you sure delete this rule?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.rules.deleteRule(rule.id);
          setRules(rules.filter(({ id }) => id !== rule.id));
          toast.success('Rule was deleted successfully');
        } catch (error) {
          toast.error('Rule was not deleted');
        }
      },
    });
  };

  const ruleCount = (pagination.pageSize || 0) * (pagination.current || 0);
  const ruleItem = (ruleCount || 0) - ((pagination.pageSize || 0) - 1);

  const handleChange = (value: string[]) => {
    setColumnsRules(value);
    const newItems = [...(columnsRules || []), ...(columnsRulesEtc || [])].filter((item) => !value.includes(item));
    setColumnsRulesEtc(newItems);
  };

  React.useEffect(() => {
    if (user?.id && columnsRules) {
      api.administration.updateColumnsRules(user.id, { columns: columnsRules });
    }
  }, [columnsRules]);
  return (
    <Tabs type="card" size="large" defaultActiveKey={'rules'}>
      <TabPane tab="Rules" key="rules">
        <Card
          title={`Upgrade Rules: ${ruleItem}-${ruleCount} / ${pagination.total}`}
          extra={
            <Space className={styles.item}>
              <div className={styles.filter}>
                <Select
                  className={styles.selectDevice}
                  allowClear
                  optionFilterProp="deviceTypes"
                  placeholder="Select a Device Type"
                  onChange={(e: DeviceType['id']) => {
                    setSelectedDeviceType(e);
                    setSelectedSuper(undefined);
                    setPagination({
                      ...pagination,
                      current: 1,
                    });
                  }}
                  value={selectedDeviceType}
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {deviceTypes?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.deviceName}
                    </Option>
                  ))}
                </Select>
                <Select
                  className={styles.selectDevice}
                  placeholder="Select SuperGroup"
                  onChange={(e: SuperGroup['id']) => {
                    setSelectedSuper(e);
                    setPagination({
                      ...pagination,
                      current: 1,
                    });
                  }}
                  allowClear
                  showSearch
                  value={selectedSuper}
                  filterOption={selectSearchFilter}
                >
                  {superGroups?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
                <Search
                  onChange={(e) => {
                    setSearchResult(e.target.value);
                  }}
                  placeholder="Search"
                  allowClear
                  enterButton
                  size="middle"
                  onSearch={() => handleSearch()}
                />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '300px max-content',
                  gridGap: '10px',
                  alignItems: 'center',
                }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%', minWidth: '300px', maxWidth: '700px' }}
                  placeholder="Please select columns"
                  filterOption={selectSearchFilter}
                  value={columnsRules}
                  onChange={handleChange}
                  maxTagCount={2}
                >
                  {columnsRules?.map((item) => (
                    <Select.Option key={item}>{item}</Select.Option>
                  ))}{' '}
                  {columnsRulesEtc?.map((item) => (
                    <Select.Option key={item}>{item}</Select.Option>
                  ))}
                </Select>
                <Button type="primary" onClick={() => history.push(Navigation.CreateRule)}>
                  Add
                </Button>
              </div>
            </Space>
          }
          bodyStyle={{ padding: 0 }}
        >
          <Table
            loading={loading}
            dataSource={rules}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey={(record: RulesWithDeviceType) => record.id}
            scroll={{ x: 1200 }}
          >
            {columnsRules?.includes('Device Type') && (
              <Column
                align="center"
                title="Device Type"
                dataIndex="deviceTypeName"
                render={(text, record: RulesWithDeviceType) => record?.etc?.deviceTypeName}
              />
            )}
            {columnsRules?.includes('Name') && (
              <Column
                align="center"
                title="Name"
                dataIndex="name"
                key="Name"
                render={(text, record: RulesWithDeviceType) => record?.name}
              />
            )}
            {columnsRules?.includes('Ui Version') && (
              <Column
                align="center"
                title="Ui Version"
                dataIndex="etc.uiVersion"
                render={(text, record: RulesWithDeviceType) => record?.etc?.uiVersion}
              />
            )}
            {columnsRules?.includes('FW Version') && (
              <Column
                align="center"
                title="FW Version"
                dataIndex="etc.firmwareVersion"
                render={(text, record: RulesWithDeviceType) => record.etc?.firmwareVersion}
              />
            )}
            {columnsRules?.includes('Loader Version') && (
              <Column
                align="center"
                title="Loader Version"
                dataIndex="etc.loaderVersion"
                render={(text, record: RulesWithDeviceType) => record.etc?.loaderVersion}
              />
            )}
            {columnsRules?.includes('Super group') && (
              <Column
                align="center"
                title="Scope"
                dataIndex="superGroupName"
                ellipsis
                render={(text, record: RulesWithDeviceType) => record.etc?.superGroupName}
              />
            )}
            {selectedDeviceType && isShowCustomFirmware1Version && columnsRules?.includes('CFW1') && (
              <Column
                align="center"
                title={deviceSetting?.fwExtraName1}
                dataIndex="etc.customFirmware1Version"
                render={(text, record: RulesWithDeviceType) => record?.etc?.customFirmware1Version}
              />
            )}
            {selectedDeviceType && isShowCustomFirmware2Version && columnsRules?.includes('CFW2') && (
              <Column
                align="center"
                title={deviceSetting?.fwExtraName2}
                dataIndex="etc.customFirmware2Version"
                render={(text, record: RulesWithDeviceType) => record.etc?.customFirmware2Version}
              />
            )}
            {selectedDeviceType && isShowCustomFirmware3Version && columnsRules?.includes('CFW3') && (
              <Column
                align="center"
                title={deviceSetting?.fwExtraName3}
                dataIndex="etc.customFirmware3Version"
                render={(text, record: RulesWithDeviceType) => record.etc?.customFirmware3Version}
              />
            )}
            {columnsRules?.includes('Status') && (
              <Column
                align="center"
                title="Status"
                dataIndex="status"
                key="status"
                render={(text, record: RulesWithDeviceType) => {
                  const statusInfo = getRuleStatus(record);
                  const content = getStatusPopoverContent(record, statusInfo);

                  return (
                    <Popover content={content} trigger="hover">
                      <Tag color={statusInfo.color} style={{ cursor: 'pointer' }}>
                        {statusInfo.message}
                      </Tag>
                    </Popover>
                  );
                }}
              />
            )}
            {columnsRules?.includes('Configuration') && (
              <Column
                align="center"
                title="Configuration"
                dataIndex="configuration"
                key="configuration"
                render={(text, record: RulesWithDeviceType) => {
                  const config = getRuleConfiguration(record);
                  const content = getConfigurationPopoverContent(config, record);

                  const buttonStyle = {
                    fontWeight: 'bold' as const,
                    fontSize: '11px',
                    padding: '2px 6px',
                    height: '24px',
                    borderRadius: '4px',
                  };

                  const buttons = [
                    {
                      show:
                        !config.hasExtended &&
                        !config.hasCompare &&
                        !config.hasTimeWindow &&
                        !config.hasChaining &&
                        !record.forceUpdate &&
                        !(record.sendAllFirmwares && record?.deviceTypeSendAllFirmwares),
                      label: 'V',
                    },
                    { show: config.hasExtended, label: 'EV' },
                    { show: config.hasCompare, label: 'C' },
                    { show: config.hasTimeWindow, label: 'TW' },
                    { show: config.hasChaining, label: 'CR' },
                    { show: record.forceUpdate, label: 'F' },
                    { show: record.sendAllFirmwares && record?.deviceTypeSendAllFirmwares, label: 'A' },
                  ];

                  return (
                    <Popover content={content} trigger="hover">
                      <Space size={4}>
                        {buttons
                          .filter((btn) => btn.show)
                          .map((btn) => (
                            <Button key={btn.label} style={buttonStyle} size="small" type="primary">
                              {btn.label}
                            </Button>
                          ))}
                      </Space>
                    </Popover>
                  );
                }}
              />
            )}

            <Column
              align="center"
              width="100px"
              fixed="right"
              title="Action"
              key="action"
              render={(text, newRules: RulesWithDeviceType) =>
                newRules.stbGroups && newRules.stbGroups?.length >= 1 ? (
                  <FormOutlined onClick={() => history.push(Navigation.UpdateRule.replace(':id', newRules.id))} />
                ) : (
                  <Space size="middle">
                    <FormOutlined onClick={() => history.push(Navigation.UpdateRule.replace(':id', newRules.id))} />
                    <DeleteOutlined onClick={() => showDeleteConfirm(newRules)} />
                  </Space>
                )
              }
            />
          </Table>
        </Card>
      </TabPane>
      <TabPane tab="Unused Rules" key="unused">
        <UnusedRule />
      </TabPane>
    </Tabs>
  );
};
export default React.memo(Rules);
